import React, { useEffect } from 'react';
import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet';
import Navbar from './Navbar';
import Footer from './Footer';
import { TbTrendingUp, TbBuildingSkyscraper, TbLeaf, TbBatteryCharging } from 'react-icons/tb';

function CommercialSolar() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  return (
    <>
      <Helmet>
        <title>Commercial Solar Panel Installation | Lunar Electric | Business Solar Solutions India</title>
        <meta name="description" content="Transform your business with commercial solar panel installation from Lunar Electric. Reduce operational costs, improve sustainability, and get expert commercial solar solutions across India." />
        <meta name="keywords" content="commercial solar panels, business solar installation, commercial solar India, office solar power, industrial solar, commercial solar cost, business solar solutions, commercial solar ROI, Lunar Electric commercial solar" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta property="og:title" content="Commercial Solar Panel Installation | Lunar Electric" />
        <meta property="og:description" content="Transform your business with commercial solar panel installation from Lunar Electric. Reduce operational costs and improve sustainability." />
        <meta property="og:url" content="https://lunarelectric.in/commercial-solar" />
        <meta name="twitter:title" content="Commercial Solar Panel Installation | Lunar Electric" />
        <meta name="twitter:description" content="Transform your business with commercial solar panel installation from Lunar Electric. Reduce operational costs and improve sustainability." />
        <link rel="canonical" href="https://lunarelectric.in/commercial-solar" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Commercial Solar Panel Installation",
          "provider": {
            "@type": "Organization",
            "name": "Lunar Electric",
            "url": "https://lunarelectric.in"
          },
          "areaServed": {
            "@type": "Country",
            "name": "India"
          },
          "description": "Professional commercial solar panel installation services for businesses across India with multiple procurement models (CAPEX, OPEX, RESCO), tax benefits, and excellent ROI",
          "offers": {
            "@type": "Offer",
            "priceRange": "₹35-50 per watt",
            "priceCurrency": "INR"
          }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://lunarelectric.in/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Commercial Solar",
              "item": "https://lunarelectric.in/commercial-solar"
            }
          ]
        })}</script>
      </Helmet>
      <a
        href="#main-content"
        className="skip-to-main"
        style={{ position: 'absolute', left: '-9999px', zIndex: 9999 }}
        onFocus={(e) => (e.target.style.left = '0')}
      >
        Skip to main content
      </a>
      <div className="commercial-solar-page" id="main-content">
        <Navbar />

        {/* Hero Section */}
        <section className="commercial-hero">
          <div className="container">
            <div className="hero-content">
              <h1>Commercial Solar Panel Installation</h1>
              <p className="hero-subtitle">Power Your Business with Sustainable Solar Energy</p>
              <p className="hero-description">
                Reduce your operational costs and improve your business sustainability with our expert commercial solar panel
                installation services. Get transparent pricing, government incentives, and comprehensive maintenance from Lunar
                Electric.
              </p>
              <div className="hero-buttons">
                <Link to="/solar-calculator" className="btn-primary">
                  Calculate Business Savings
                </Link>
                <Link to="/callback" className="btn-secondary">
                  Get Commercial Quote
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="commercial-benefits">
          <div className="container">
            <h2>Why Choose Commercial Solar with Lunar Electric?</h2>
            <div className="benefits-grid">
              <div className="benefit-card">
                <TbTrendingUp className="specialization-icon" />
                <h3>Reduce Operational Costs by 60-80%</h3>
                <p>
                  Significantly lower your electricity bills and improve your bottom line with our efficient commercial solar
                  solutions.
                </p>
              </div>
              <div className="benefit-card">
                <TbBuildingSkyscraper className="specialization-icon" />
                <h3>Scalable Business Solutions</h3>
                <p>
                  From small offices to large industrial facilities, we provide scalable solar solutions that grow with your
                  business.
                </p>
              </div>
              <div className="benefit-card">
                <TbLeaf className="specialization-icon" />
                <h3>Enhance Corporate Sustainability</h3>
                <p>
                  Demonstrate your commitment to environmental responsibility and meet corporate sustainability goals.
                </p>
              </div>
              <div className="benefit-card">
                <TbBatteryCharging className="specialization-icon" />
                <h3>Reliable Power Supply</h3>
                <p>
                  Ensure uninterrupted power supply for your business operations with our robust solar energy systems.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Industries Section */}
        <section className="commercial-industries">
          <div className="container">
            <h2>Industries We Serve</h2>
            <div className="industries-grid">
              <div className="industry-card">
                <h3>Manufacturing</h3>
                <p>Power your production facilities with reliable solar energy and reduce manufacturing costs.</p>
              </div>
              <div className="industry-card">
                <h3>Retail &amp; Shopping Centers</h3>
                <p>Light up your retail spaces with clean, cost-effective solar power solutions.</p>
              </div>
              <div className="industry-card">
                <h3>Hotels &amp; Hospitality</h3>
                <p>Provide sustainable hospitality services with our commercial solar installations.</p>
              </div>
              <div className="industry-card">
                <h3>Educational Institutions</h3>
                <p>Power schools and colleges with clean energy while teaching students about sustainability.</p>
              </div>
              <div className="industry-card">
                <h3>Healthcare Facilities</h3>
                <p>Ensure reliable power for hospitals and clinics with our commercial solar systems.</p>
              </div>
              <div className="industry-card">
                <h3>IT &amp; Office Buildings</h3>
                <p>Keep your servers and office equipment running with clean, reliable solar power.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ROI Section */}
        <section className="commercial-roi">
          <div className="container">
            <h2>Impressive Return on Investment</h2>
            <div className="roi-content">
              <div className="roi-stats">
                <div className="stat">
                  <h3>3-5 Years</h3>
                  <p>Average Payback Period</p>
                </div>
                <div className="stat">
                  <h3>25+ Years</h3>
                  <p>System Lifespan</p>
                </div>
                <div className="stat">
                  <h3>15-20%</h3>
                  <p>Annual ROI</p>
                </div>
              </div>
              <div className="roi-description">
                <p>
                  Our commercial solar installations typically pay for themselves within 3-5 years, providing 20+ years of free
                  electricity and significant cost savings for your business.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="commercial-cta">
          <div className="container">
            <h2>Ready to Transform Your Business?</h2>
            <p>Join leading Indian businesses that have already made the switch to commercial solar energy.</p>
            <div className="cta-buttons">
              <Link to="/callback" className="btn-primary">
                Get Commercial Quote
              </Link>
              <Link to="/solar-calculator" className="btn-secondary">
                Calculate Business ROI
              </Link>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}

export default CommercialSolar;
